import React, { useState } from 'react';
import { Button, useNotify, useRefresh } from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import MuiTextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';

import { APIService } from '../../common/api.service';

// ─── Reason labels (readable, with fallback for unknown codes) ────────────────

const CHEAT_REASON_LABELS: Record<string, string> = {
  distance_limit_exceeded: 'Distance limit exceeded',
  speed_limit_exceeded: 'Speed limit exceeded',
  track_api_failed: 'Flagged by track analysis',
};

export const cheatReasonLabel = (reason?: string | null): string => {
  if (!reason) {
    return 'Cheat suspected';
  }
  return CHEAT_REASON_LABELS[reason] ?? `Flagged (${reason})`;
};

// ─── Unit formatters ──────────────────────────────────────────────────────────

// seconds (integer) → h:mm:ss
export const formatTime = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined || isNaN(Number(seconds))) {
    return '—';
  }
  const s = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// meters (integer) → "21.10 km"
export const formatDistance = (meters?: number | null): string => {
  if (meters === null || meters === undefined || isNaN(Number(meters))) {
    return '—';
  }
  return `${(Number(meters) / 1000).toFixed(2)} km`;
};

const formatResult = (result?: { time?: number; distance?: number } | null): string => {
  if (!result) {
    return '—';
  }
  return `${formatTime(result.time)} / ${formatDistance(result.distance)}`;
};

// ─── Badge ────────────────────────────────────────────────────────────────────

export const CheatBadge = ({ record }: any) => {
  if (!record?.cheatSuspect) {
    return null;
  }
  return (
    <Chip
      label={cheatReasonLabel(record.cheatReason)}
      size="small"
      style={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 600 }}
    />
  );
};

// ─── Overrule dialog ──────────────────────────────────────────────────────────

const isNonNegativeInt = (value: string): boolean => {
  if (value.trim() === '') {
    return false;
  }
  return /^\d+$/.test(value.trim());
};

export const OverruleCheatButton = ({ record }: any) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'config' | 'confirm'>('config');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<any>(null);

  const [mode, setMode] = useState<'restore' | 'manual'>('restore');
  const [time, setTime] = useState('');
  const [distance, setDistance] = useState('');

  const resetState = () => {
    setStep('config');
    setInfo(null);
    setMode('restore');
    setTime('');
    setDistance('');
    setLoading(false);
    setSubmitting(false);
  };

  const handleOpen = async () => {
    resetState();
    setOpen(true);
    setLoading(true);
    try {
      const data = await APIService.getCheatInfo(record.id);
      setInfo(data);
      // Default to manual entry when nothing can be restored.
      if (!data?.original) {
        setMode('manual');
      }
    } catch (e) {
      notify('Could not load cheat info', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setOpen(false);
  };

  const hasOriginal = Boolean(info?.original);
  const timeValid = time.trim() === '' || isNonNegativeInt(time);
  const distanceValid = distance.trim() === '' || isNonNegativeInt(distance);
  const manualHasValue = isNonNegativeInt(time) || isNonNegativeInt(distance);
  const manualValid = timeValid && distanceValid && manualHasValue;
  const configValid = mode === 'restore' ? hasOriginal : manualValid;

  const appliedSummary = (): string => {
    if (mode === 'restore') {
      return `${formatResult(info?.original)} (from ${info?.original?.source ?? 'original'})`;
    }
    const parts: string[] = [];
    if (isNonNegativeInt(time)) {
      parts.push(`time ${formatTime(parseInt(time.trim(), 10))}`);
    }
    if (isNonNegativeInt(distance)) {
      parts.push(`distance ${formatDistance(parseInt(distance.trim(), 10))}`);
    }
    return `${parts.join(', ')} (manual — unspecified values unchanged)`;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const body: { restoreOriginal?: boolean; time?: number; distance?: number } = {};
    if (mode === 'manual') {
      if (isNonNegativeInt(time)) {
        body.time = parseInt(time.trim(), 10);
      }
      if (isNonNegativeInt(distance)) {
        body.distance = parseInt(distance.trim(), 10);
      }
    } else {
      body.restoreOriginal = true;
    }

    try {
      const result = await APIService.clearCheat(record.id, body);
      const restoredFrom = result?.restoredFrom;
      if (restoredFrom === 'none') {
        notify(
          'Flag cleared, but no original could be recovered — the penalized values are still in place. Retry with a manual value.',
          'warning',
          {},
          false,
          10000
        );
      } else {
        notify(`Cheat flag cleared (restored from: ${restoredFrom ?? 'unknown'})`, 'success');
      }
      setOpen(false);
      refresh();
    } catch (e) {
      notify('Could not overrule cheat flag', 'error');
      setSubmitting(false);
    }
  };

  if (!record?.cheatSuspect) {
    return null;
  }

  return (
    <>
      <Button label="Overrule cheat flag" onClick={handleOpen} style={{ color: '#f44336' }} />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Overrule cheat flag</DialogTitle>
        <DialogContent>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
              <CircularProgress size={20} />
              <span>Re-running track analysis…</span>
            </div>
          ) : step === 'config' ? (
            <>
              {/* Comparison */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Detected:</strong> {cheatReasonLabel(info?.cheatReason ?? record.cheatReason)}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Current (penalized):</strong> {formatResult(info?.current)}
                </div>
                <div>
                  <strong>Original:</strong>{' '}
                  {hasOriginal ? (
                    <>
                      {formatResult(info.original)} <em>({info.original.source})</em>
                    </>
                  ) : (
                    <span style={{ color: '#f44336' }}>Not recoverable — enter values manually</span>
                  )}
                </div>
              </div>

              {/* Mode selection (Button toggle — avoids MUI SwitchBase layout issues) */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Button
                  label="Restore original"
                  variant={mode === 'restore' ? 'contained' : 'outlined'}
                  color="primary"
                  disabled={!hasOriginal}
                  onClick={() => setMode('restore')}
                />
                <Button
                  label="Enter manually"
                  variant={mode === 'manual' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setMode('manual')}
                />
              </div>

              {mode === 'restore' && hasOriginal && (
                <DialogContentText>
                  Will apply the original result: {formatResult(info.original)} ({info.original.source}).
                </DialogContentText>
              )}

              {mode === 'manual' && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <MuiTextField
                    label="Time [seconds]"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    error={!timeValid}
                    helperText={time.trim() === '' ? 'Leave blank to keep current' : timeValid ? formatTime(parseInt(time, 10)) : 'Integer ≥ 0'}
                    fullWidth
                    variant="outlined"
                  />
                  <MuiTextField
                    label="Distance [meters]"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    error={!distanceValid}
                    helperText={
                      distance.trim() === '' ? 'Leave blank to keep current' : distanceValid ? formatDistance(parseInt(distance, 10)) : 'Integer ≥ 0'
                    }
                    fullWidth
                    variant="outlined"
                  />
                </div>
              )}
            </>
          ) : (
            /* Confirmation step */
            <>
              <DialogContentText style={{ marginBottom: 12 }}>
                The finished result will be set to: <strong>{appliedSummary()}</strong>.
              </DialogContentText>
              <DialogContentText style={{ color: '#b26a00' }}>
                This re-sends the athlete's finish mail / push notification and draws their prize. This cannot be undone
                automatically.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'config' ? (
            <>
              <Button label="Cancel" onClick={handleClose} disabled={submitting} />
              <Button label="Continue" onClick={() => setStep('confirm')} disabled={loading || !configValid} color="primary" />
            </>
          ) : (
            <>
              <Button label="Back" onClick={() => setStep('config')} disabled={submitting} />
              <Button
                label={submitting ? 'Applying…' : 'Overrule & apply'}
                onClick={handleSubmit}
                disabled={submitting}
                style={!submitting ? { color: '#f44336' } : {}}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
